import autobind from 'class-autobind';

import BaseHorseReduction from 'atg/api/BaseHorseReduction';
import StickyTemplate from 'atg/vendor/StickyTemplate/1.0/StickyTemplate';
import { settings } from 'shared/redux/selectors';
import { getStore } from 'shared/redux/store';
import * as Text from 'shared/utils/Text';

import BaseView from './BaseView';

const $ = window.$;

export default class BaseHorseReductionView extends BaseView {
  constructor() {
    super(...arguments);
    autobind(this, BaseHorseReductionView.prototype);
  }

  getGridClass() {
    return 'view-base-horse-reduction';
  }

  // ---------------------------------------- MENU ----------------------------------------

  getViewTitle() {
    return Text.get(
      'view-base-reduction',
      this.getNumber(BaseHorseReductionView),
    );
  }

  getMenuTitle() {
    return Text.get(
      'view-base-reduction',
      this.getNumber(BaseHorseReductionView),
    );
  }

  getMenuGroupTitle() {
    return Text.get('view-group-base-reduction');
  }

  getMenuAddItemTitle() {
    return Text.get('view-base-add');
  }

  getMenuMaxNumberOfViews() {
    return this.getBaseHorseReductionSet().getMaxNumberOfBaseHorseReductions();
  }

  getMenuTogglable() {
    return true;
  }

  getMenuDeletable() {
    return true;
  }

  // ---------------------------------------- GRID ----------------------------------------

  getGridCellInfo(position, rowCache) {
    const api = this.getAPI();

    let race;
    let betRow;
    let gameRow;
    let resultRow;
    let betConfig;

    if (rowCache) {
      race = rowCache.race;
      betRow = rowCache.betRow;
      gameRow = rowCache.gameRow;
      resultRow = rowCache.resultRow;
      betConfig = rowCache.betConfig;
    } else {
      // eslint-disable-next-line no-param-reassign
      rowCache = {};

      race = rowCache.race = api.raceInformation.getRace(position.row);
      betRow = rowCache.betRow = api.betInformation.getRow(position.row);
      gameRow = rowCache.gameRow = api.gameInformation.getRow(position.row);
      resultRow = rowCache.resultRow = api.resultInformation.getRow(
        position.row,
      );
      betConfig = rowCache.betConfig = api.betInformation.getBetConfig();
    }

    const startNumber = betRow
      ? betRow.getStartNumberAtPosition(position.column)
      : position.column + 1;

    const startIndex = startNumber - 1;

    const start = race.getStart(startIndex);

    const viewIndex = this.getIndex();

    const info = {
      rowCache: rowCache,
    };

    if (start) {
      info.number = start.getStartNumber();

      const horse = start.getHorse();
      if (horse) {
        info.horseName = horse.getName();
      }

      const jockey = start.getJockey();
      if (jockey) {
        info.jockeyTrainerName = jockey.getName();
      }

      const originalJockey = start.getOriginalJockey();
      if (originalJockey) {
        info.originalJockey = originalJockey.getName();
      }
    }

    if (
      resultRow &&
      settings.showRankingResults(getStore().getState()) &&
      betConfig.getShowResultRow(position.row)
    ) {
      info.winner = resultRow.isWinner(startIndex);
    }

    const gameRowCellInfo = this.getGameRowCellInfo(gameRow, startIndex);
    Object.assign(info, gameRowCellInfo);

    if (betRow && position.column < betRow.data.numberOfHorses) {
      info.checkbox = betRow.getBaseHorseReductionState(startIndex, viewIndex);
    }

    return info;
  }

  onGridCellTapped(position, event) {
    if ($(event.target).closest('.checkbox span').length > 0) {
      this.onGridCheckButtonPressed(position);
      return;
    }
    this.getGrid().highlightCell(position);

    const buttons = [
      {
        icon: 'button-last',
        onPress: this.onGridLastButtonPressed,
      },
    ];

    if (
      this.getAPI().betInformation.getRow(position.row).data.numberOfHorses >
      position.column
    ) {
      buttons.push({
        icon: 'button-check',
        onPress: this.onGridCheckButtonPressed,
      });
    }

    buttons.push(...this.makeHorseButtons(position));

    this.getGrid().showCellButtons(position, buttons);
  }

  onGridCheckButtonPressed(position) {
    const willShowLockedAlert = this.showAlertWhenLocked();

    const grid = this.getGrid();
    grid.closeHighlight();

    if (!willShowLockedAlert) {
      const viewIndex = this.getIndex();
      const betRow = this.getAPI().betInformation.getRow(position.row);
      const startNumber = betRow.getStartNumberAtPosition(position.column);
      const startIndex = startNumber - 1;
      const state = betRow.getBaseHorseReductionState(startIndex, viewIndex);

      // Dont allow marking of horses outside the system
      if (position.column >= betRow.data.numberOfHorses) {
        grid.closeHighlight();
        return;
      }

      betRow.setBaseHorseReductionState(startIndex, viewIndex, !state);

      grid.updateRow(position.row);

      this.getAPI().scheduleBetUpdate();
    }
  }

  onRowWasUpdated(row) {
    const betRow = this.getAPI().betInformation.getRow(row);
    const viewIndex = this.getIndex();
    const rowLength = betRow.data.order.length;
    let needUpdate = false;
    let startNumber, startIndex, state, outside, i;

    for (i = 0; i < rowLength; ++i) {
      startNumber = betRow.getStartNumberAtPosition(i);
      startIndex = startNumber - 1;
      state = betRow.getBaseHorseReductionState(startIndex, viewIndex);
      outside = i >= betRow.data.numberOfHorses;
      if (state && outside) {
        betRow.setBaseHorseReductionState(startIndex, viewIndex, false);
        needUpdate = true;
      }
    }

    if (needUpdate) {
      this.getGrid().updateRow(row);
    }
  }

  // ---------------------------------------- DATA ----------------------------------------

  getIndex() {
    return super.getIndex(BaseHorseReductionView);
  }

  getBaseHorseReductionSet() {
    return this.getAPI()
      .betInformation.getReductionSet()
      .getBaseHorseReductionSet();
  }

  getBaseHorseReduction() {
    const reductionSet = this.getBaseHorseReductionSet();

    let reduction;

    if (reductionSet) {
      const index = this.getIndex();

      const numberOfReductions = reductionSet.getNumberOfBaseHorseReductions();

      if (index < numberOfReductions) {
        reduction = reductionSet.getBaseHorseReduction(index);
      }
    }

    if (!reduction) {
      reduction = new BaseHorseReduction();
    }

    return reduction;
  }

  getItem() {
    return this.getBaseHorseReduction();
  }

  addItem() {
    return this.getBaseHorseReductionSet().addBaseHorseReduction();
  }

  deleteItem() {
    const api = this.getAPI();
    const index = this.getIndex();

    this.getBaseHorseReductionSet().deleteBaseHorseReduction(index);

    const numberOfRows = api.betInformation.getNumberOfRows();
    for (let row = 0; row < numberOfRows; row++) {
      api.betInformation.getRow(row).deleteBaseHorseReduction(index);
    }

    this.getAPI().scheduleBetUpdate();
  }

  getNumberOfItems() {
    const set = this.getBaseHorseReductionSet();
    return set ? set.getNumberOfBaseHorseReductions() : 0;
  }

  // ---------------------------------------- BOTTOM INFO DELEGATE METHODS ----------------------------------------

  getBottomInfoContent() {
    const $conditions = (this.$conditions = StickyTemplate.jq(
      'bottom-conditions-base-horse-reduction',
    ));

    $conditions.find('form').event('submit', this.onFormSubmit);

    this.updateConditionsBaseHorse();
    return $conditions;
  }

  // ---------------------------------------- POINT SUMS ----------------------------------------

  updateConditionsBaseHorse() {
    const reduction = this.getBaseHorseReduction();

    const $wrapper = this.$conditions.find('.base-horse');
    const $rows = $wrapper.find('.rows');

    $rows.empty();

    const $row = StickyTemplate.jq(
      'bottom-conditions-base-horse-reduction-row',
      {
        '%Min%': Text.get('bottom-conditions-Min'),
        '%max%': Text.get('bottom-conditions-max'),
        '%includes-winner%': Text.get(
          'bottom-conditions-base-horse-reduction-includes-winner',
        ),
        '%comment%': Text.get('comment-field-label'),
      },
    );

    this.addNumberOfRacesInput($row);

    if (reduction) {
      $row.find('.min-winners').val(reduction.getMinWinners());
      $row.find('.max-winners').val(reduction.getMaxWinners());
      $row.find('.comment').val(reduction.getComment());
    }

    $rows.append($row);

    $wrapper.find('input, select').reevent('change', this.onConditionsChanged);
  }

  // ---------------------------------------- FORM TO DATA ----------------------------------------

  onConditionsChanged() {
    const $form = this.$conditions.find('form');

    const formData = $form.formData();

    const reduction = this.getBaseHorseReduction();

    const baseHorse = reduction;
    const baseHorseData = formData.baseHorse;

    baseHorse.setMinWinners(baseHorseData.minWinners);
    baseHorse.setMaxWinners(baseHorseData.maxWinners);
    baseHorse.setComment(baseHorseData.comment);

    this.getAPI().scheduleBetUpdate();
  }
}
